import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { User as OidcUser } from "oidc-client-ts";
import { Client, clientsApi } from "@/lib/api";
import { resolveCycleFlow } from "@/lib/orderCycle";

// Define the structure of our application-specific user profile
export type UserRole = "customer" | "backoffice" | "Administrator";

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  clientCode?: string;
  clientName?: string;
  avatar?: string;
}

// Define the context type
interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isCustomer: boolean;
  isBackoffice: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  selectedCycleYear: number | null;
  setSelectedCycleYear: (cycleYear: number | null) => void;
  // Anos com ciclo Open (aceitam pedido) e anos com ciclo Published (só visualização/simulação),
  // vindos de GET /clients/cycle via resolveCycleFlow. `null` = ainda não carregado.
  openCycleYears: number[] | null;
  viewOnlyCycleYears: number[] | null;
  logout: () => void;
}

// Helper function to transform OIDC user profile into our app's User type
const transformOidcUser = (oidcUser: OidcUser | null): User | null => {
  if (!oidcUser?.profile) {
    return null;
  }

  const profile = oidcUser.profile;
  const scope = oidcUser.scope;

  // Type assertion for role to ensure it matches our defined UserRole
  const role = profile["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] as UserRole;

  return {
    id: profile.sub, // 'sub' is the standard OIDC claim for user ID
    name: `${profile.given_name} ${profile.family_name}`,
    email: profile.email || "No email available",
    role: role,
    clientCode: scope, // Assuming scope contains clientCode
    clientName: profile.name || `${profile.given_name} ${profile.family_name}`,
    avatar: profile.picture || `${profile.given_name?.[0] || ''}${profile.family_name?.[0] || ''}`,
  };
};

// Create the context
export const UserContext = createContext<UserContextType | undefined>(undefined);

// Create the provider component
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const auth = useAuth();
  const [user, setUser] = useState<User | null>(() => transformOidcUser(auth.user || null));
  const [selectedClient, setSelectedClientState] = useState<Client | null>(null);
  const [selectedCycleYear, setSelectedCycleYear] = useState<number | null>(new Date().getFullYear());
  // Anos com ciclo comercial Open, vindos de GET /clients/cycle. `null` = ainda não carregado.
  const [openCycleYears, setOpenCycleYears] = useState<number[] | null>(null);
  // Anos com ciclo comercial Published (visualização/simulação, sem pedido).
  const [viewOnlyCycleYears, setViewOnlyCycleYears] = useState<number[] | null>(null);

  // Trocar de cliente invalida o ciclo escolhido anteriormente — cada cliente
  // pode ter uma ambiguidade de ciclo diferente, e o backend deve decidir de novo.
  // Quando há exatamente 1 ciclo Open (caso comum), resolve automaticamente em vez de
  // deixar null — evita reabrir o CyclePickerDialog à toa a cada troca de cliente.
  const setSelectedClient = (client: Client | null) => {
    setSelectedClientState(client);
    setSelectedCycleYear(openCycleYears?.length === 1 ? openCycleYears[0] : null);
  };

  // Busca os ciclos comerciais disponíveis uma vez por sessão autenticada e resolve
  // `selectedCycleYear` automaticamente quando há exatamente 1 ciclo Open — substitui o
  // "chute" pelo ano corrente do sistema usado antes deste endpoint existir. Ambíguo
  // (0 ou 2+ ciclos Open) continua resolvido pelo fluxo existente de erro 409
  // `cycle_required` (ver useCycleRequiredHandler/CyclePickerDialog).
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    let cancelled = false;

    clientsApi
      .getCycles()
      .then((res) => {
        if (cancelled) return;
        const cycles = res.cycles || [];
        const openYears = cycles
          .filter((cycle) => resolveCycleFlow(cycle.status) === "order")
          .map((cycle) => cycle.year);
        const viewOnlyYears = cycles
          .filter((cycle) => resolveCycleFlow(cycle.status) === "view-only")
          .map((cycle) => cycle.year);
        setOpenCycleYears(openYears);
        setViewOnlyCycleYears(viewOnlyYears);
        if (openYears.length === 1) {
          setSelectedCycleYear(openYears[0]);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar ciclos comerciais abertos:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated]);
  // const [ selectedClient, setSelectedClient ] = useState<Client | null>( () => {
  //   const savedClientJSON = localStorage.getItem( 'selectedSchoolUnit' );
  //   if ( savedClientJSON ) {
  //     try {
  //       return JSON.parse( savedClientJSON );
  //     } catch ( error ) {
  //       console.error( "Erro ao restaurar unidade do localStorage:", error );
  //       localStorage.removeItem( 'selectedSchoolUnit' );
  //       return null;
  //     }
  //   }
  //   return null;
  // } );

  // useEffect(() => {
  //     if (selectedClient) {
  //         localStorage.setItem('selectedSchoolUnit', JSON.stringify(selectedClient));
  //     } else {
  //         localStorage.removeItem('selectedSchoolUnit');
  //     }
  // }, [selectedClient]);

  useEffect(() => {
    setIsLoading(true);
    // When OIDC auth state changes, re-transform and set the user
    if (auth.isAuthenticated && auth.user) {
      setUser(transformOidcUser(auth.user));
      setIsLoading(false);
    } else {
      setUser(null);
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user]);

  const logout = () => {
    // The user will be set to null via the useEffect when isAuthenticated changes
    auth.signoutRedirect();
  };

  const value: UserContextType = {
    user,
    isAuthenticated: !!user,
    isCustomer: user?.role === "customer",
    isBackoffice: user?.role === "backoffice" || user?.role === "Administrator",
    isAdmin: user?.role === "Administrator",
    selectedClient,
    isLoading,
    logout,
    setSelectedClient,
    selectedCycleYear,
    setSelectedCycleYear,
    openCycleYears,
    viewOnlyCycleYears,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Create the consumer hook
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
